import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box, Button, Chip, Stack, Switch, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  clearSupportCallTranscription,
  getSupportCallTranscriptText,
  getSupportCallTranscriptionState,
  isSupportCallTranscriptionSupported,
  startSupportCallTranscription,
  stopSupportCallTranscription,
  subscribeSupportCallTranscription,
} from 'src/supportPortal/components/call/supportCallTranscription'

// Opt-in live transcription panel. Reflects the module singleton so the transcript persists across
// minimize/restore. Honest about scope: this is agent-side (Web Speech) until a server STT feed
// on the call recording lands — see supportCallTranscription.js.
export default function SupportCallTranscriptPanel({ callActive }) {
  const supported = isSupportCallTranscriptionSupported()
  const [state, setState] = useState(getSupportCallTranscriptionState())
  const scrollRef = useRef(null)

  useEffect(() => subscribeSupportCallTranscription(setState), [])

  // Auto-stop when the call ends, but keep the transcript on screen.
  useEffect(() => {
    if (!callActive && state.active) {
      stopSupportCallTranscription()
    }
  }, [callActive, state.active])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [state.finals, state.interim])

  function handleToggle(event) {
    if (event.target.checked) {
      startSupportCallTranscription()
    } else {
      stopSupportCallTranscription()
    }
  }

  async function handleCopy() {
    const text = getSupportCallTranscriptText()
    if (!text) {
      toast.info('Nothing to copy yet.')
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      toast.success('Transcript copied')
    } catch (error) {
      toast.error('Could not copy the transcript')
    }
  }

  if (!supported) {
    return (
      <Box sx={{ p: 1.5, borderRadius: '12px', border: '1px dashed rgba(148, 163, 184, 0.5)', backgroundColor: '#f8fafc' }}>
        <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
          Live transcription is not supported in this browser. Try a recent Chrome/Edge, or wait for the
          server-side transcription feed.
        </Typography>
      </Box>
    )
  }

  const hasContent = state.finals.length > 0 || !!state.interim

  return (
    <Stack spacing={1} sx={{ height: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Switch
            size="small"
            checked={state.active}
            onChange={handleToggle}
            disabled={!callActive && !state.active}
          />
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155' }}>
            {state.active ? 'Transcribing…' : 'Live transcription'}
          </Typography>
          {state.active ? (
            <GraphicEqRoundedIcon
              sx={{
                fontSize: 16,
                color: '#4f46e5',
                animation: 'supportCallEq 1s ease-in-out infinite',
                '@keyframes supportCallEq': { '0%,100%': { opacity: 0.4 }, '50%': { opacity: 1 } },
              }}
            />
          ) : null}
        </Stack>
        <Stack direction="row" spacing={0.25}>
          <Button
            size="small"
            startIcon={<ContentCopyRoundedIcon sx={{ fontSize: 15 }} />}
            onClick={handleCopy}
            disabled={!hasContent}
            sx={{ textTransform: 'none', fontWeight: 700, color: '#64748b', minWidth: 0 }}
          >
            Copy
          </Button>
          <Button
            size="small"
            startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={clearSupportCallTranscription}
            disabled={!hasContent}
            sx={{ textTransform: 'none', fontWeight: 700, color: '#64748b', minWidth: 0 }}
          >
            Clear
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: '#94a3b8' }}>
        <InfoOutlinedIcon sx={{ fontSize: 13 }} />
        <Typography sx={{ fontSize: 10.5, lineHeight: 1.3 }}>
          Agent-side only (best-effort). Two-sided transcription needs the server feed.
        </Typography>
      </Stack>

      {state.error ? (
        <Typography sx={{ fontSize: 11.5, color: '#b91c1c' }}>{state.error}</Typography>
      ) : null}

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 120,
          overflowY: 'auto',
          p: 1.25,
          borderRadius: '12px',
          border: '1px solid rgba(226, 232, 240, 0.9)',
          backgroundColor: '#f8fafc',
        }}
      >
        {!hasContent ? (
          <Typography sx={{ fontSize: 12.5, color: '#94a3b8' }}>
            {state.active
              ? 'Listening… start speaking to see the transcript.'
              : 'Turn on live transcription during the call to capture what you say.'}
          </Typography>
        ) : (
          <Stack spacing={0.75}>
            {state.finals.map((segment) => (
              <Stack key={segment.id} direction="row" spacing={0.75} alignItems="flex-start">
                <Chip
                  size="small"
                  label="You"
                  sx={{ height: 18, fontSize: 10, fontWeight: 700, color: '#4338ca', backgroundColor: 'rgba(79, 70, 229, 0.1)' }}
                />
                <Typography sx={{ fontSize: 12.5, color: '#1e293b', lineHeight: 1.45 }}>{segment.text}</Typography>
              </Stack>
            ))}
            {state.interim ? (
              <Typography sx={{ fontSize: 12.5, color: '#94a3b8', fontStyle: 'italic', pl: '46px' }}>
                {state.interim}
              </Typography>
            ) : null}
          </Stack>
        )}
      </Box>
    </Stack>
  )
}
